import { FieldLabel, Input, Select, Textarea } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { withTheme } from "@rjsf/core";
import type { FieldTemplateProps, WidgetProps as RjsfWidgetProps } from "@rjsf/utils";
import React from "react";

type WidgetProps = {
  options: RjsfWidgetProps["options"] & {
    enumOptions?: Array<{ label: string; value: unknown; }>;
    enumDisabled?: Array<unknown>;
    emptyValue?: unknown;
    inputType?: string;
    rows?: number;
  };
  type?: string;
} & RjsfWidgetProps;

const CustomTextWidget = (props: WidgetProps) => {
  const {
    disabled,
    id,
    onBlur,
    onChange,
    onFocus,
    options,
    placeholder,
    rawErrors,
    readonly,
    required,
    schema,
    type,
    value,
  } = props;

  let inputType = schema.type === "string" ? "text" : String(schema.type);
  if (schema.format !== undefined && typeof schema.format === "string") {
    if (["email", "hostname", "ipv4", "ipv6"].includes(schema.format)) {
      inputType = schema.format;
    } else if (["uri", "url"].includes(schema.format)) {
      inputType = "url";
    } else if (schema.format === "date-time") {
      inputType = "datetime-local";
    } else if (["date", "time"].includes(schema.format)) {
      inputType = schema.format;
    }
  }
  if (options.inputType !== undefined && typeof options.inputType === "string") {
    inputType = options.inputType;
  }
  if (type !== undefined && typeof type === "string") {
    inputType = type;
  }

  // Use auto-sizing textarea for plain text strings
  if (inputType === "text") {
    return (
      <Textarea.Autosize
        id={id}
        name={id}
        placeholder={placeholder}
        required={required}
        readOnly={readonly}
        disabled={disabled ?? false}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? options.emptyValue : e.target.value)}
        onBlur={(e) => onBlur(id, e.target.value)}
        onFocus={(e) => onFocus(id, e.target.value)}
        aria-invalid={Boolean(rawErrors !== undefined && rawErrors.length > 0)}
      />
    );
  }

  return (
    <Input
      id={id}
      name={id}
      placeholder={placeholder}
      required={required}
      readOnly={readonly}
      disabled={disabled ?? false}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? options.emptyValue : e.target.value)}
      onBlur={(e) => onBlur(id, e.target.value)}
      onFocus={(e) => onFocus(id, e.target.value)}
      type={inputType}
      aria-invalid={Boolean(rawErrors !== undefined && rawErrors.length > 0)}
    />
  );
};

const CustomTextareaWidget = (props: WidgetProps) => {
  const {
    disabled,
    id,
    onBlur,
    onChange,
    onFocus,
    options,
    placeholder,
    rawErrors,
    readonly,
    required,
    value,
  } = props;

  const _onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value === "" ? options.emptyValue : event.target.value);
  };
  const _onBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    onBlur(id, event.target.value);
  };
  const _onFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    onFocus(id, event.target.value);
  };

  return (
    <Textarea.Autosize
      id={id}
      name={id}
      placeholder={placeholder}
      value={value ?? ""}
      required={required}
      disabled={disabled ?? false}
      readOnly={readonly}
      onChange={_onChange}
      onBlur={_onBlur}
      onFocus={_onFocus}
      aria-invalid={Boolean(rawErrors !== undefined && rawErrors.length > 0)}
      minRows={typeof options.rows === "number" ? options.rows : 1}
    />
  );
};

const CustomSelectWidget = (props: WidgetProps) => {
  const {
    disabled,
    id,
    onBlur,
    onChange,
    onFocus,
    options,
    placeholder,
    rawErrors,
    readonly,
    required,
    schema,
    value,
  } = props;

  const actualPlaceholder = placeholder ?? schema.description;

  return (
    <Select
      name={id}
      value={String(value ?? "")}
      required={required}
      disabled={(disabled ?? false) || (readonly ?? false)}
      onValueChange={(v) =>
        onChange(v === "" && options.emptyValue !== undefined ? options.emptyValue : v)}
      items={Object.fromEntries(
        (options.enumOptions ?? []).map(({ label: l, value: val }) => [String(val), l]),
      )}
    >
      <Select.Trigger
        id={id}
        aria-invalid={(rawErrors !== undefined && rawErrors.length > 0) || undefined}
        onBlur={() => onBlur(id, String(value ?? ""))}
        onFocus={() => onFocus(id, String(value ?? ""))}
      >
        <Select.Value placeholder={actualPlaceholder ?? ""} />
      </Select.Trigger>
      <Select.Content>
        {options.enumOptions?.map(({ label: optLabel, value: optValue }, i: number) => (
          <Select.Item
            key={`${String(optValue)}-${i}`}
            value={String(optValue)}
            disabled={options.enumDisabled?.includes(optValue)}
          >
            {optLabel}
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  );
};

const CustomFieldTemplate = (props: FieldTemplateProps) => {
  const {
    children,
    description,
    displayLabel = true,
    errors,
    hidden = false,
    id,
    label,
    rawErrors,
    required = false,
    schema,
  } = props;

  if (id === "root") {
    return <React.Fragment key={id}>{children}</React.Fragment>;
  }

  if (hidden) {
    return <div className="hidden">{children}</div>;
  }

  const fieldDescription = description ?? schema.description;
  const fieldTitle = schema.title;
  const hasErrors = rawErrors !== undefined && rawErrors.length > 0;

  return (
    <div className="flex flex-col gap-1.5 mb-4">
      {displayLabel && (label !== "" || fieldTitle !== undefined) && (
        <React.Fragment>
          <FieldLabel
            htmlFor={id}
            required={required}
            {...(typeof fieldDescription === "string" && fieldDescription !== ""
              && { tooltip: fieldDescription })}
          >
            {fieldTitle ?? label}
          </FieldLabel>

          {(schema.minLength !== undefined
            || schema.maxLength !== undefined
            || schema.minimum !== undefined
            || schema.maximum !== undefined
            || schema.format !== undefined
            || schema.pattern !== undefined) && (
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {schema.minLength !== undefined && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  Min: {schema.minLength}
                </span>
              )}
              {schema.maxLength !== undefined && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  Max: {schema.maxLength}
                </span>
              )}
              {schema.minimum !== undefined && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  Min: {schema.minimum}
                </span>
              )}
              {schema.maximum !== undefined && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  Max: {schema.maximum}
                </span>
              )}
              {schema.format !== undefined && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  Format: {schema.format}
                </span>
              )}
              {schema.pattern !== undefined && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  Pattern
                </span>
              )}
            </div>
          )}
        </React.Fragment>
      )}

      {children}

      {hasErrors && <div className="space-y-1">{errors}</div>}
    </div>
  );
};

const CustomTitleTemplate = (_props: { title: string; required?: boolean; }) => {
  return null;
};

const CustomDescriptionTemplate = (_props: { description: string; id?: string; }) => {
  return null;
};

const CustomObjectFieldTemplate = (props: {
  title: string;
  description: string;
  properties: Array<{
    content: React.ReactElement;
    name: string;
    readonly: boolean;
    disabled: boolean;
    required: boolean;
  }>;
  required: boolean;
  disabled: boolean;
  readonly: boolean;
  idSchema: { $id: string; };
}) => {
  const { properties, title, description, idSchema } = props;
  const isNested = idSchema.$id !== "root" && idSchema.$id.includes("_");
  const nestingLevel = idSchema.$id.split("_").length - 1;

  if (isNested) {
    return (
      <div
        className={cn(
          "relative rounded-lg border-2 border-dashed border-muted-foreground/30 p-4",
          nestingLevel > 1 && "ml-4 bg-muted/5",
        )}
      >
        {title && (
          <div className="absolute -top-3 left-3 bg-background px-2">
            <span className="text-sm font-medium text-muted-foreground">{title}</span>
          </div>
        )}
        {description && <p className="text-xs text-muted-foreground mb-3">{description}</p>}
        <div className="space-y-4">
          {properties.map((element) => <div key={element.name}>{element.content}</div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {properties.map((element) => <div key={element.name}>{element.content}</div>)}
    </div>
  );
};

const CustomErrorListTemplate = (
  props: { errors: Array<{ message: string; stack: string; }>; },
) => {
  const { errors } = props;

  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <div className="mb-4 p-3 rounded-md bg-destructive/10 border border-destructive/20">
      <h4 className="text-sm font-medium text-destructive mb-2">
        Please fix the following errors:
      </h4>
      <ul className="space-y-1">
        {errors.map((error) => (
          <li key={error.message} className="text-xs text-destructive-text flex items-start gap-1">
            <span className="text-destructive mt-0.5">•</span>
            <span>{error.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CustomFieldErrorTemplate = (props: { errors: Array<string>; }) => {
  const { errors } = props;
  if (!errors || errors.length === 0) {
    return null;
  }
  return (
    <div className="space-y-1">
      {errors.map((error) => (
        <p key={error} className="text-xs text-destructive-text flex items-center gap-1">
          <span className="text-destructive">⚠</span>
          {error}
        </p>
      ))}
    </div>
  );
};

const rjsfTheme = {
  widgets: {
    TextWidget: CustomTextWidget,
    TextareaWidget: CustomTextareaWidget,
    SelectWidget: CustomSelectWidget,
    URLWidget: CustomTextWidget,
    EmailWidget: CustomTextWidget,
    DateWidget: CustomTextWidget,
    DateTimeWidget: CustomTextWidget,
    TimeWidget: CustomTextWidget,
    PasswordWidget: CustomTextWidget,
    UpDownWidget: CustomTextWidget,
    RangeWidget: CustomTextWidget,
  },
  templates: {
    FieldTemplate: CustomFieldTemplate,
    ErrorListTemplate: CustomErrorListTemplate,
    FieldErrorTemplate: CustomFieldErrorTemplate,
    TitleTemplate: CustomTitleTemplate,
    DescriptionTemplate: CustomDescriptionTemplate,
    ObjectFieldTemplate: CustomObjectFieldTemplate,
  },
};

export const ThemedForm = withTheme(rjsfTheme);
